import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckIcon } from 'lucide-react';
import { bookingUrl } from '../data/content';
type PackageId = 'kickstart' | 'launch' | 'expert';
const tabBase = 'flex h-full flex-col rounded-3xl border p-4 text-left transition-all duration-200 sm:p-5';
const tabOn = 'border-slate-900 bg-gradient-to-b from-butter-50 to-white ring-1 ring-slate-900 dark:border-butter-300 dark:from-slate-800 dark:to-slate-900 dark:ring-butter-300';
const tabOff = 'border-slate-200 bg-pearl-50 hover:-translate-y-0.5 hover:border-butter-400 dark:border-slate-800 dark:bg-slate-800';
const featureRow = 'flex gap-2.5 text-sm text-slate-700 dark:text-slate-200';
const checkClass = 'mt-0.5 h-4 w-4 flex-none text-butter-600 dark:text-butter-300';
const panelClass = 'mt-4 rounded-4xl border border-slate-200 bg-pearl-50 p-6 dark:border-slate-800 dark:bg-slate-800 sm:p-8';
const featureList = 'mt-5 grid gap-2.5 sm:grid-cols-2 sm:gap-x-8';
const buttonSolid = 'mt-6 inline-flex w-full items-center justify-center rounded-full bg-slate-900 px-6 py-3.5 text-sm font-semibold text-pearl-50 transition-colors hover:bg-slate-700 dark:bg-butter-300 dark:text-slate-900 dark:hover:bg-butter-200 sm:w-auto';
const panelMotion = {
  initial: {
    opacity: 0,
    y: 8
  },
  animate: {
    opacity: 1,
    y: 0
  },
  exit: {
    opacity: 0,
    y: -6
  },
  transition: {
    duration: 0.25,
    ease: [0.22, 1, 0.36, 1] as const
  }
};
export function Pricing() {
  const [selected, setSelected] = useState<PackageId>('launch');
  const tabClass = (id: PackageId) => `${tabBase} ${selected === id ? tabOn : tabOff}`;
  return <section id="pricing" className="bg-white py-14 dark:bg-slate-900 sm:py-20" data-id="element-175" data-mp="3n5afqljf1hbn">
      <div className="mx-auto max-w-6xl px-5 sm:px-8" data-id="element-176" data-mp="3rz6cxlruv05r">
        <div className="max-w-2xl" data-id="element-177" data-mp="2uj9897c5i3or">
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-plum-500 dark:text-plum-300" data-id="element-178" data-mp="2ui7ma7tljws9">
            Pricing
          </p>
          <h2 className="mt-3 font-display text-3xl font-semibold tracking-tight text-slate-900 dark:text-pearl-50 sm:text-4xl" data-id="element-179" data-mp="2rgla9rkk1r8k">
            Three ways to work together
          </h2>
          <p className="mt-4 text-base text-slate-600 dark:text-slate-300 sm:text-lg" data-id="element-180" data-mp="316obug5qsdii">
            Tap a package to see what is included. Final pricing is shared on the free consult call
            and in writing before you commit. Pause or cancel anytime, and unused sessions are
            refunded.
          </p>
        </div>

        {/* Package selector */}
        <div role="tablist" aria-label="Coaching packages" className="mt-8 grid grid-cols-3 gap-2.5 sm:mt-10 sm:gap-4" data-id="element-181" data-mp="23kgx4dfq54sf">
          <button type="button" role="tab" aria-selected={selected === 'kickstart'} aria-controls="package-panel" onClick={() => setSelected('kickstart')} className={tabClass('kickstart')} data-id="element-182" data-mp="2ie3otfcq52mm">
            <span className="font-display text-base font-semibold text-slate-900 dark:text-pearl-50 sm:text-xl" data-id="element-183" data-mp="3txg452josi0k">
              Kickstart
            </span>
            <span className="mt-2 font-display text-2xl font-semibold text-slate-900 dark:text-pearl-50 sm:text-3xl" data-id="element-184" data-mp="a41zmsj8mmt">
              $$
            </span>
            <span className="mt-1 text-[11px] leading-snug text-slate-500 dark:text-slate-400 sm:text-sm" data-id="element-185" data-mp="57409bj54l3d">
              one 60-min session
            </span>
          </button>

          <button type="button" role="tab" aria-selected={selected === 'launch'} aria-controls="package-panel" onClick={() => setSelected('launch')} className={tabClass('launch')} data-id="element-186" data-mp="3rgyjev7jo3q2">
            <span className="font-display text-base font-semibold text-slate-900 dark:text-pearl-50 sm:text-xl" data-id="element-187" data-mp="nyn2vh995x4v">
              Launch
            </span>
            <span className="mt-2 font-display text-2xl font-semibold text-slate-900 dark:text-pearl-50 sm:text-3xl" data-id="element-188" data-mp="ea52300z3q18">
              $$
            </span>
            <span className="mt-1 text-[11px] leading-snug text-slate-500 dark:text-slate-400 sm:text-sm" data-id="element-189" data-mp="11y6a87u8p4g4">
              2 sessions over 1 month
            </span>
            <span className="mt-2 self-start rounded-full bg-gradient-to-r from-butter-300 to-butter-200 px-2.5 py-1 text-[9px] font-semibold uppercase tracking-wide text-butter-700 dark:from-butter-400 dark:to-butter-300 dark:text-slate-900 sm:text-[11px]" data-id="element-190" data-mp="3f81tx0503cqg">
              CLIENT FAVORITE
            </span>
          </button>

          <button type="button" role="tab" aria-selected={selected === 'expert'} aria-controls="package-panel" onClick={() => setSelected('expert')} className={tabClass('expert')} data-id="element-191" data-mp="3192cacfwny3s">
            <span className="font-display text-base font-semibold text-slate-900 dark:text-pearl-50 sm:text-xl" data-id="element-192" data-mp="3szyfsmg2sqee">
              Expert
            </span>
            <span className="mt-2 font-display text-2xl font-semibold text-slate-900 dark:text-pearl-50 sm:text-3xl" data-id="element-193" data-mp="3o5u984aq2kdu">
              $$$
            </span>
            <span className="mt-1 text-[11px] leading-snug text-slate-500 dark:text-slate-400 sm:text-sm" data-id="element-194" data-mp="kjwxpbv2nw1g">
              4+ sessions over 3 months
            </span>
          </button>
        </div>

        {/* Expanded detail */}
        <div id="package-panel" role="tabpanel" data-id="element-195" data-mp="1yh7zhkyq9lfv">
          <AnimatePresence mode="wait" initial={false} data-id="element-196" data-mp="23dg9kjy2iog6">
            {selected === 'kickstart' && <motion.div key="kickstart" {...panelMotion} className={panelClass} data-id="element-197" data-mp="27g3813p8v7mc">
                <p className="max-w-2xl leading-relaxed text-slate-600 dark:text-slate-300" data-id="element-198" data-mp="2cewlwx8g10n1">
                  A single deep-dive to iron out one main goal. Best fit for someone who has an
                  upcoming interview, presentation, or has general coaching questions.
                </p>

                <ul className={featureList} data-id="element-199" data-mp="2r3sowfvn7ngs">
                  <li className={featureRow} data-id="element-200" data-mp="2r4viwwo9i60x">
                    <CheckIcon className={checkClass} aria-hidden="true" data-id="element-201" data-mp="2r66kqqy5d3pe" />
                    <span data-id="element-202" data-mp="2v4md7aczz4a7">60-minute 1:1 coaching session</span>
                  </li>
                  <li className={featureRow} data-id="element-203" data-mp="305tohjecmllm">
                    <CheckIcon className={checkClass} aria-hidden="true" data-id="element-204" data-mp="353leg7vfnxn6" />
                    <span data-id="element-205" data-mp="351hbbs5cz2ny">Written action plan within 48 hours</span>
                  </li>
                  <li className={featureRow} data-id="element-206" data-mp="151lrq025ro68">
                    <CheckIcon className={checkClass} aria-hidden="true" data-id="element-207" data-mp="102rc16zhmfqc" />
                    <span data-id="element-208" data-mp="100eo8d71tb5d">2 weeks of follow-up support within business hours</span>
                  </li>
                  <li className={featureRow} data-id="element-209" data-mp="1a0f9w5se43cq">
                    <CheckIcon className={checkClass} aria-hidden="true" data-id="element-210" data-mp="19znccpf8fvjt" />
                    <span data-id="element-211" data-mp="1nrahzfv2egf9">10% off Launch package if upgraded</span>
                  </li>
                </ul>

                <a href={bookingUrl} target="_blank" rel="noopener noreferrer" className={buttonSolid} data-id="element-212" data-mp="1jr06jnoi6xt0">
                  Start with Kickstart
                </a>
              </motion.div>}

            {selected === 'launch' && <motion.div key="launch" {...panelMotion} className={panelClass} data-id="element-213" data-mp="1ss7zrrc5x8g8">
                <p className="max-w-2xl leading-relaxed text-slate-600 dark:text-slate-300" data-id="element-214" data-mp="3b70qzmvcryt">
                  Our core program that is time efficient and effective. Best for someone looking to
                  receive support on 2-3 goals.
                </p>

                <ul className={featureList} data-id="element-215" data-mp="2ezc4l8q29pm2">
                  <li className={featureRow} data-id="element-216" data-mp="2eyajscj8tor5">
                    <CheckIcon className={checkClass} aria-hidden="true" data-id="element-217" data-mp="2f16e7qvvtvau" />
                    <span data-id="element-218" data-mp="20a5wev98wp21">Two, 60-minute 1:1 coaching sessions</span>
                  </li>
                  <li className={featureRow} data-id="element-219" data-mp="257y0enn601ml">
                    <CheckIcon className={checkClass} aria-hidden="true" data-id="element-220" data-mp="1ra10pidezf3p" />
                    <span data-id="element-221" data-mp="1rbu5es4gvp92">Written action plan within 24 hours of each session</span>
                  </li>
                  <li className={featureRow} data-id="element-222" data-mp="1w6hp8doizvhb">
                    <CheckIcon className={checkClass} aria-hidden="true" data-id="element-223" data-mp="1hcmx4jclakst" />
                    <span data-id="element-224" data-mp="1maeq7goz0cyl">Async support via Text or Email outside of business hours if needed</span>
                  </li>
                  <li className={featureRow} data-id="element-225" data-mp="3bqnsuvcueroo">
                    <CheckIcon className={checkClass} aria-hidden="true" data-id="element-226" data-mp="3bnrvb84wq50r" />
                    <span data-id="element-227" data-mp="3go5v62wcu8np">10% off Expert package if upgraded</span>
                  </li>
                </ul>

                <a href={bookingUrl} target="_blank" rel="noopener noreferrer" className={buttonSolid} data-id="element-228" data-mp="24ej1bcat0x6b">
                  Start with Launch
                </a>
              </motion.div>}

            {selected === 'expert' && <motion.div key="expert" {...panelMotion} className={panelClass} data-id="element-229" data-mp="1un6b0p4nmf2e">
                <p className="max-w-2xl leading-relaxed text-slate-600 dark:text-slate-300" data-id="element-230" data-mp="1pq65cfbpvebt">
                  For a longer runway — internship season, senior year, or a first-job pivot with
                  real support.
                </p>

                <ul className={featureList} data-id="element-231" data-mp="1bqetol8oaa0z">
                  <li className={featureRow} data-id="element-232" data-mp="1br6qu0hx4ftd">
                    <CheckIcon className={checkClass} aria-hidden="true" data-id="element-233" data-mp="16rttgltf7qcj" />
                    <span data-id="element-234" data-mp="16todlxwvbefk">Everything in Launch</span>
                  </li>
                  <li className={featureRow} data-id="element-235" data-mp="36463bkr04twf">
                    <CheckIcon className={checkClass} aria-hidden="true" data-id="element-236" data-mp="315usp7svufd5" />
                    <span data-id="element-237" data-mp="313ift91zev6y">Certified LinkedIn badge</span>
                  </li>
                  <li className={featureRow} data-id="element-238" data-mp="38okry6hzctyq">
                    <CheckIcon className={checkClass} aria-hidden="true" data-id="element-239" data-mp="3dnxhiz08kf50" />
                    <span data-id="element-240" data-mp="3dlufthpq0xkg">Peer support group access via WhatsApp</span>
                  </li>
                  <li className={featureRow} data-id="element-241" data-mp="3ih0ap613pmko">
                    <CheckIcon className={checkClass} aria-hidden="true" data-id="element-242" data-mp="3nea04lohpy8i" />
                    <span data-id="element-243" data-mp="2pqhztiu4ygwp">Bonus free friend session</span>
                  </li>
                </ul>

                <a href={bookingUrl} target="_blank" rel="noopener noreferrer" className={buttonSolid} data-id="element-244" data-mp="2up30fjdav2lo">
                  Start with Expert
                </a>
              </motion.div>}
          </AnimatePresence>
        </div>
      </div>
    </section>;
}